import { t } from '@lingui/macro';
import color from '@decisiv/design-tokens/lib/color';
import toColorString from 'polished/lib/color/toColorString';

const AVATAR_PALETTE = [
  toColorString(color.status.information.dark),
  toColorString(color.status.success.dark),
  toColorString(color.status.danger.dark),
  toColorString(color.status.warning.dark),
  toColorString(color.base.alaskanHusky),
  toColorString(color.base.charcoal),
];

export const CATEGORY_COLORS = {
  Config: 'var(--if-cat-config)',
  Endpoint: 'var(--if-cat-endpoint)',
  Credentials: 'var(--if-warning-ink)',
  Security: 'var(--if-cat-security)',
  Lifecycle: 'var(--if-danger-ink)',
};

// Category/author accents are ink-on-surface colours (light in night mode),
// so chips tint the surface with them instead of filling behind white text.
export const tint = (accent, pct) =>
  `color-mix(in srgb, ${accent} ${pct}%, var(--if-surface-1))`;

const SECRET_KEYWORDS = [
  'password',
  'token',
  'secret',
  'api_key',
  'private_key',
  'passphrase',
  'credential',
];

export function isSecretField(fieldName) {
  const lower = (fieldName || '').toLowerCase();
  return SECRET_KEYWORDS.some((k) => lower.includes(k));
}

export function inferLifecycleEvent(fieldName, to) {
  if (fieldName === 'discarded_at') {
    if (to !== null && to !== undefined) return 'removed';
    return 'restored';
  }
  if (fieldName === 'enabled') {
    if (to === null) return 'removed';
    if (to === true) return 'enabled';
    if (to === false) return 'disabled';
  }
  return null;
}

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) % 2147483647;
  }
  return Math.abs(hash);
}

export function getAvatarColor(name) {
  if (!name) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[hashString(name) % AVATAR_PALETTE.length];
}

export function getInitials(name) {
  if (!name) return '?';
  const parts = name.replace(/[^a-zA-Z ]/g, '').split(/(?=[A-Z])|\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function inferChangeType(fieldName, from, to) {
  if (typeof from === 'boolean' || typeof to === 'boolean') return 'boolean';
  if (fieldName.includes('url') || fieldName.includes('endpoint')) return 'url';
  if (fieldName.includes('header') || Array.isArray(from) || Array.isArray(to))
    return 'json';
  try {
    if (typeof from === 'string') JSON.parse(from);
    if (typeof to === 'string') JSON.parse(to);
    if (
      (typeof from === 'string' && from.startsWith('{')) ||
      (typeof to === 'string' && to.startsWith('{'))
    )
      return 'json';
  } catch (_e) {
    // not json
  }
  if (typeof from === 'object' || typeof to === 'object') return 'json';
  if (
    !Number.isNaN(Number(from)) &&
    !Number.isNaN(Number(to)) &&
    from !== '' &&
    to !== ''
  )
    return 'number';
  return 'string';
}

export function inferChangeCategory(fieldName) {
  if (fieldName === 'enabled' || fieldName === 'updated_by') return 'Config';
  if (fieldName.includes('url') || fieldName.includes('endpoint'))
    return 'Endpoint';
  if (
    fieldName.includes('header') ||
    fieldName.includes('api_key') ||
    fieldName.includes('credential')
  )
    return 'Credentials';
  if (
    fieldName.includes('ssl') ||
    fieldName.includes('verify') ||
    fieldName.includes('cert')
  )
    return 'Security';
  return 'Config';
}

// Cascades are attributed to the acting user, so without this the audit trail
// reads as if someone edited each linked integration by hand.
export function propagationOf(metadata) {
  if (!metadata?.propagated) return null;

  return {
    action: metadata.action,
    sharedSettingName: metadata.shared_setting_name,
    sourceIntegrationUuid: metadata.source_integration_uuid,
  };
}

// Whole sentences rather than an interpolated verb: a translator needs the
// clause, not fragments assembled in English word order.
export function propagationSummary({ action, sharedSettingName: group }) {
  if (!group) {
    return t`Automatically synced by a Shared Setting, not changed by hand in this integration.`;
  }

  switch (action) {
    case 'shared_setting_location_add':
      return t`Automatically added by the "${group}" Shared Setting, not changed by hand in this integration.`;
    case 'shared_setting_location_enable':
      return t`Automatically enabled by the "${group}" Shared Setting, not changed by hand in this integration.`;
    case 'shared_setting_location_disable':
      return t`Automatically disabled by the "${group}" Shared Setting, not changed by hand in this integration.`;
    case 'shared_setting_location_removal':
      return t`Automatically removed by the "${group}" Shared Setting, not changed by hand in this integration.`;
    case 'shared_setting_location_seed':
      return t`Values automatically synced by the "${group}" Shared Setting, not changed by hand in this integration.`;
    default:
      return t`Automatically synced by the "${group}" Shared Setting, not changed by hand in this integration.`;
  }
}

export function flattenHistories(histories, fallbackSrmAccountId) {
  const allEntries = Array.isArray(histories)
    ? histories
    : Object.values(histories);

  const sorted = [...allEntries].sort(
    (a, b) => new Date(b.attributes.date) - new Date(a.attributes.date),
  );

  const rows = [];
  sorted.forEach((entry, sessionIndex) => {
    const {
      author,
      date,
      changes,
      metadata,
      srm_account_id: entrySrmAccountId,
    } = entry.attributes;
    const srmAccountId = entrySrmAccountId || fallbackSrmAccountId;
    const propagation = propagationOf(metadata);
    if (!changes) return;
    const changeList = Array.isArray(changes) ? changes : [changes];
    changeList.forEach((change, changeIndex) => {
      const lifecycleEvent = inferLifecycleEvent(change.fieldName, change.to);
      rows.push({
        sessionIndex,
        changeIndex,
        author,
        date,
        srmAccountId,
        fieldName: change.fieldName,
        from: change.from,
        to: change.to,
        type: inferChangeType(change.fieldName, change.from, change.to),
        category: lifecycleEvent
          ? 'Lifecycle'
          : inferChangeCategory(change.fieldName),
        lifecycleEvent,
        propagation,
        sessionEntry: entry,
      });
    });
  });

  return rows;
}

export function formatValue(value) {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}
